export const IMPORT_DESTINATION_FIELDS = [
  {
    value: "name",
    label: "Nombre",
    required: true,
    description: "Texto visible que identifica el ítem. Es obligatorio para poder importar.",
  },
  {
    value: "description",
    label: "Descripción",
    description: "Detalle descriptivo o comercial del ítem; complementa al nombre y puede quedar vacío.",
  },
  {
    value: "category",
    label: "Categoría",
    description: "Agrupación principal usada para ordenar y filtrar los ítems del catálogo.",
  },
  {
    value: "subcategory",
    label: "Subcategoría",
    description: "Segundo nivel opcional de clasificación dentro de la categoría principal.",
  },
  {
    value: "price",
    label: "Precio",
    description: "Valor numérico de venta. WebProfit admite coma o punto decimal y formatos numéricos localizados compatibles.",
  },
  {
    value: "stock",
    label: "Stock inicial / a conciliar",
    description: "Existencia inicial o a conciliar. No se escribe directo en Product: el commit la registra mediante movimientos ERP.",
    warning: true,
  },
  {
    value: "code",
    label: "Código",
    description: "Identificador tipo SKU, código interno o código de barras. También es la clave del modo crear o actualizar por código.",
  },
  {
    value: "unit",
    label: "Unidad",
    description: "Unidad de medida o venta del ítem, por ejemplo un., kg o lt.",
  },
  {
    value: "itemKind",
    label: "Tipo de ítem",
    description: "Clasifica el registro como producto, servicio, insumo, gasto, activo o cargo.",
  },
  {
    value: "active",
    label: "Activo",
    description: "Estado sí/no que define si el ítem queda habilitado. Admite sí/no, true/false y 1/0.",
  },
  {
    value: "publicVisible",
    label: "Visible públicamente",
    description: "Estado sí/no que define si puede mostrarse en el catálogo público. Admite sí/no, true/false y 1/0.",
  },
  {
    value: "sellable",
    label: "Vendible",
    description: "Estado sí/no que habilita el ítem para ventas. Admite sí/no, true/false y 1/0.",
  },
  {
    value: "purchasable",
    label: "Comprable",
    description: "Estado sí/no que habilita el ítem para compras. Admite sí/no, true/false y 1/0.",
  },
];

export const IMPORT_FIELD_BY_VALUE = Object.freeze(
  Object.fromEntries(IMPORT_DESTINATION_FIELDS.map((field) => [field.value, field])),
);

const HEADER_ALIASES = {
  name: ["articulo", "artículo", "nombre", "producto", "item", "ítem", "descripcion articulo", "descripción artículo"],
  description: ["descripcion", "descripción", "detalle", "observaciones"],
  category: ["rubro", "categoria", "categoría", "familia"],
  subcategory: ["subrubro", "subcategoria", "subcategoría", "subfamilia"],
  price: ["pventa", "p venta", "p.venta", "precio", "precio venta", "venta", "importe"],
  stock: ["exist", "exist.", "existencia", "existencias", "stock", "cantidad", "saldo"],
  code: ["codbar", "cod.bar", "codigo", "código", "sku", "barcode", "codigo barra", "código barra", "codigo de barras", "código de barras"],
  unit: ["unidad", "un", "u.m.", "um", "medida"],
  itemKind: ["tipo", "tipo item", "tipo ítem", "clase"],
  active: ["activo", "habilitado", "estado"],
  publicVisible: ["publico", "público", "visible", "publicVisible"],
  sellable: ["vendible", "venta habilitada"],
  purchasable: ["comprable", "compra habilitada"],
};

const AMBIGUOUS_NAME_HEADERS = new Set(["articulo", "item"]);
const AMBIGUOUS_ACTIVE_HEADERS = new Set(["estado"]);
const AMBIGUOUS_KIND_HEADERS = new Set(["tipo", "clase"]);
const BOOLEAN_VALUES = new Set(["1", "0", "true", "false", "si", "sí", "yes", "activo", "active", "no", "inactivo", "inactive"]);
const ITEM_KIND_VALUES = new Set(["producto", "product", "servicio", "service", "insumo", "supply", "gasto", "expense", "activo", "asset", "cargo", "charge"]);

function normalizeHeader(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function cleanSamples(samples = []) {
  const result = [];
  const seen = new Set();
  for (const raw of Array.isArray(samples) ? samples : []) {
    const value = String(raw ?? "").trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    result.push(value);
    if (result.length >= 3) break;
  }
  return result;
}

export function deriveImportColumnSamples({ headers = [], columnSamples, sampleRows = [] } = {}) {
  const backendSamples = columnSamples && typeof columnSamples === "object" && !Array.isArray(columnSamples)
    ? columnSamples
    : null;

  return Object.fromEntries(headers.map((header, columnIndex) => {
    if (backendSamples && Object.prototype.hasOwnProperty.call(backendSamples, header)) {
      return [header, cleanSamples(backendSamples[header])];
    }
    return [
      header,
      cleanSamples((Array.isArray(sampleRows) ? sampleRows : []).map((row) => row?.[columnIndex])),
    ];
  }));
}

function headerMatchScore(normalizedHeader, alias) {
  const normalizedAlias = normalizeHeader(alias);
  if (!normalizedAlias) return 0;
  if (normalizedHeader === normalizedAlias) return 100;
  if (` ${normalizedHeader} `.includes(` ${normalizedAlias} `)) return 76;
  return 0;
}

function samplesLookLikeCodes(samples = []) {
  const values = cleanSamples(samples);
  if (!values.length) return false;
  let strong = 0;
  let descriptive = 0;

  for (const value of values) {
    const compact = value.replace(/\s+/g, "");
    const hasLetter = /[A-Za-z]/.test(value);
    const hasDigit = /\d/.test(value);
    const compactPattern = /^[A-Za-z0-9._/-]+$/.test(value);
    const wordCount = value.trim().split(/\s+/).filter(Boolean).length;
    if (hasLetter && hasDigit && compactPattern && compact.length <= 48) strong += 1;
    if (wordCount >= 2 && /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/.test(value)) descriptive += 1;
  }

  return strong >= Math.ceil(values.length * 0.67) && descriptive === 0;
}

function samplesLookDescriptive(samples = []) {
  const values = cleanSamples(samples);
  if (!values.length) return false;
  const descriptive = values.filter((value) => {
    const words = value.trim().split(/\s+/).filter(Boolean);
    return words.length >= 2 && /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/.test(value) && !/^[-_./A-Za-z0-9]+$/.test(value);
  }).length;
  return descriptive >= Math.ceil(values.length * 0.67);
}

function samplesMatchSet(samples = [], acceptedValues) {
  const values = cleanSamples(samples);
  if (!values.length) return false;
  const matched = values.filter((value) => acceptedValues.has(String(value).trim().toLowerCase())).length;
  return matched >= Math.ceil(values.length * 0.67);
}

function suggestionCandidate(header, samples) {
  const normalized = normalizeHeader(header);

  if (AMBIGUOUS_NAME_HEADERS.has(normalized)) {
    if (samplesLookLikeCodes(samples)) return { target: "code", score: 86 };
    if (samplesLookDescriptive(samples)) return { target: "name", score: 84 };
    return null;
  }

  if (AMBIGUOUS_ACTIVE_HEADERS.has(normalized)) {
    return samplesMatchSet(samples, BOOLEAN_VALUES) ? { target: "active", score: 82 } : null;
  }

  if (AMBIGUOUS_KIND_HEADERS.has(normalized)) {
    return samplesMatchSet(samples, ITEM_KIND_VALUES) ? { target: "itemKind", score: 82 } : null;
  }

  let best = null;
  for (const [target, aliases] of Object.entries(HEADER_ALIASES)) {
    for (const alias of aliases) {
      const score = headerMatchScore(normalized, alias);
      if (score > (best?.score || 0)) best = { target, score };
    }
  }
  return best;
}

export function suggestImportMapping(headers = [], columnSamples = {}) {
  const mapping = Object.fromEntries(headers.map((header) => [header, "ignore"]));
  const usedTargets = new Set();
  const candidates = headers
    .map((header, index) => ({
      header,
      index,
      ...suggestionCandidate(header, columnSamples?.[header] || []),
    }))
    .filter((candidate) => candidate.target)
    .sort((a, b) => b.score - a.score || a.index - b.index);

  for (const candidate of candidates) {
    if (usedTargets.has(candidate.target)) continue;
    mapping[candidate.header] = candidate.target;
    usedTargets.add(candidate.target);
  }

  return mapping;
}

export function mappedTarget(mapping = {}, target) {
  return Object.entries(mapping).find(([, value]) => value === target)?.[0] || "";
}
